import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  ViewerContainer,
  CloseButton,
  Toolbar,
  ModeButton,
  BottomBar,
  SegChipButton,
} from './Visualizer.style';
import {
  ISegmentationLabel,
  ISegmentationLabelsParams,
  ISegmentationReference,
  ISegmentationReferencesParams,
  ISegmentationPrediction,
  ISegmentationPredictionsParams,
  IFrame,
  IPnoFrame,
  IPnoParams,
} from 'models/icardio';
import {
  useGetSegmentationReferencesQuery,
  useGetPnoQuery,
  useGetSegmentationLabelsQuery,
  useGetSegmentationPredictionsQuery,
} from '../../../../../redux/slices/icardio';
import { createUltrasoundFanOutline } from './geometry';
import { buildPnoMatrixFan, buildPnoMatrixSegmentation } from './pno';
import {
  IMAGE_HEIGHT,
  IMAGE_WIDTH,
  SCALE,
  SCALE_FACTOR,
  SEGMENTATION_CLASS_ACRONYM,
} from './constants';

export interface ThreeDViewerProps {
  onClose: () => void;
  studyIcid: string | undefined;
  dicomIcid: string | undefined;
  activeFrame: IFrame;
  imageHeight: number;
  imageWidth: number;
}

function computeGroupCenter(group: THREE.Group): THREE.Vector3 {
  const box = new THREE.Box3();
  box.makeEmpty();
  group.traverse(obj => {
    if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
      const objBox = new THREE.Box3().setFromObject(obj);
      box.union(objBox);
    }
  });
  const center = new THREE.Vector3();
  box.getCenter(center);
  return center;
}

const ThreeDViewer: React.FC<ThreeDViewerProps> = ({
  onClose,
  studyIcid,
  dicomIcid,
  activeFrame,
  imageHeight,
  imageWidth,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const segRefGroupRef = useRef<THREE.Group | null>(null);
  const segPredGroupRef = useRef<THREE.Group | null>(null);
  const segLabelGroupRef = useRef<THREE.Group | null>(null);
  const fanGroupRef = useRef<THREE.Group | null>(null);
  const textureCache = useRef<Map<string, THREE.Texture>>(new Map());

  // Query segmentation references & labels
  const segmentationReferenceParams: ISegmentationReferencesParams = { studyIcid, paginate: false };
  const { data: allSegmentationRefsResponse, isFetching: _segmentationRefsResponseIsLoading } =
    useGetSegmentationReferencesQuery(segmentationReferenceParams, { skip: !studyIcid }) as {
      data: ISegmentationReference[];
      isFetching: boolean;
    };
  const segmentationPredictionParams: ISegmentationPredictionsParams = {
    studyIcid,
    paginate: false,
  };
  const { data: allSegmentationPredsResponse, isFetching: _segmentationPredsResponseIsLoading } =
    useGetSegmentationPredictionsQuery(segmentationPredictionParams, { skip: !studyIcid }) as {
      data: ISegmentationPrediction[];
      isFetching: boolean;
    };
  const segmentationlabelParams: ISegmentationLabelsParams = { studyIcid, paginate: false };
  const { data: allSegmentationLabelsResponse, isFetching: _segmentationLabelsResponseIsLoading } =
    useGetSegmentationLabelsQuery(segmentationlabelParams, { skip: !studyIcid }) as {
      data: ISegmentationLabel[];
      isFetching: boolean;
    };
  // Query pno
  const pnoParams: IPnoParams = { dicomIcid, paginate: false };
  const { data: pnoResponse, isFetching: _pnoResponseIsLoading } = useGetPnoQuery(pnoParams, {
    skip: !dicomIcid,
  }) as {
    data: IPnoFrame[];
    isFetching: boolean;
  };
  //map frame_icids to pno arrays
  const pnoMap = useMemo(() => {
    const map = new Map<string, number[]>();
    if (Array.isArray(pnoResponse)) {
      for (const { frameIcid, pno } of pnoResponse) {
        map.set(frameIcid, pno);
      }
    }
    return map;
  }, [pnoResponse]);
  //colors for each segmentation class
  const segClassColorMap = useMemo(() => {
    const segs = [
      ...(allSegmentationLabelsResponse ?? []),
      ...(allSegmentationRefsResponse ?? []),
      ...(allSegmentationPredsResponse ?? []),
    ];
    return segs.length ? buildClassColorMap(segs) : {};
  }, [allSegmentationLabelsResponse, allSegmentationRefsResponse, allSegmentationPredsResponse]);

  //toggle ref/pred/tech visibility
  const [showSegRef, setShowSegRef] = useState<boolean>(true);
  const [showSegPred, setShowSegPred] = useState<boolean>(true);
  const [showSegLabel, setShowSegLabel] = useState<boolean>(true);
  const toggleSegRefVisibility = () => {
    setShowSegRef(prev => !prev);
  };
  const toggleSegPredVisibility = () => {
    setShowSegPred(prev => !prev);
  };
  const toggleSegLabelVisibility = () => {
    setShowSegLabel(prev => !prev);
  };
  useEffect(() => {
    if (!segRefGroupRef.current) return;
    segRefGroupRef.current.visible = showSegRef;
  }, [showSegRef]);
  useEffect(() => {
    if (!segPredGroupRef.current) return;
    segPredGroupRef.current.visible = showSegPred;
  }, [showSegPred]);
  useEffect(() => {
    if (!segLabelGroupRef.current) return;
    segLabelGroupRef.current.visible = showSegLabel;
  }, [showSegLabel]);

  //toggle segmentation class visibility
  const [visibleSegClasses, setVisibleSegClasses] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(Object.keys(segClassColorMap).map(cls => [cls, true]))
  );
  const toggleSegClassVisibility = (className: string) => {
    setVisibleSegClasses(prev => ({
      ...prev,
      [className]: !prev[className],
    }));
  };
  useEffect(() => {
    setVisibleSegClasses(prev => {
      const next = { ...prev };
      for (const cls of Object.keys(segClassColorMap)) {
        if (!(cls in next)) next[cls] = true;
      }
      return next;
    });
  }, [segClassColorMap]);

  // Select mode: hover to highlight, click to navigate
  const [selectMode, setSelectMode] = useState<boolean>(false);
  const selectModeRef = useRef<boolean>(false);
  const raycasterRef = useRef(new THREE.Raycaster());
  const hoveredLineRef = useRef<THREE.Object3D | null>(null);
  const navigate = useNavigate();
  const dicomIcidRef = useRef(dicomIcid);
  useEffect(() => {
    dicomIcidRef.current = dicomIcid;
  }, [dicomIcid]);

  useEffect(() => {
    selectModeRef.current = selectMode;
    if (!selectMode) {
      // reset opacity/linewidth on all lines
      const groups = [segLabelGroupRef.current, segRefGroupRef.current, segPredGroupRef.current];
      groups.forEach(group => {
        if (!group) return;
        group.children.forEach(child => {
          const mat = (child as THREE.Line).material as THREE.LineBasicMaterial;
          if (mat) {
            mat.transparent = true;
            mat.opacity = 1;
            (mat as any).linewidth = 1;
            mat.needsUpdate = true;
          }
        });
      });
      hoveredLineRef.current = null;
    }
  }, [selectMode]);

  // --- One-time Three.js initialization (scene, camera, renderer, controls) ---
  useEffect(() => {
    if (!containerRef.current) return;

    // Avoid double initialization (React StrictMode can run effects twice in dev)
    if (rendererRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth || 600;
    const height = container.clientHeight || 500;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x111);

    // Camera
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 0, 5);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    // OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.enablePan = true;
    controls.enableZoom = true;

    // // axes helper
    // const axesHelper = new THREE.AxesHelper(2);
    // scene.add(axesHelper);
    // camera.lookAt(axesHelper.position);
    // controls.target.copy(axesHelper.position);
    // controls.update();

    //lights
    const light1 = new THREE.DirectionalLight(0xffffff, 1);
    light1.position.set(10, 10, 10);
    scene.add(light1);
    const ambient = new THREE.AmbientLight(0x808080);
    scene.add(ambient);

    // Groups for dynamic content
    const segRefGroup = new THREE.Group();
    scene.add(segRefGroup);
    const segPredGroup = new THREE.Group();
    scene.add(segPredGroup);
    const segLabelGroup = new THREE.Group();
    scene.add(segLabelGroup);

    // fan
    const fanGroup = new THREE.Group();
    //add outline to fan
    const fanOutline = createUltrasoundFanOutline();
    fanGroup.add(fanOutline);
    fanGroupRef.current = fanGroup;
    // add rectangle for reference at image size
    const rectGeometry = new THREE.PlaneGeometry(
      IMAGE_WIDTH / SCALE_FACTOR,
      IMAGE_HEIGHT / SCALE_FACTOR
    );
    const rectMesh = new THREE.Mesh(rectGeometry);
    // Translate geometry up by half its height
    rectMesh.position.y = IMAGE_HEIGHT / SCALE_FACTOR / 2;
    //scale down
    fanGroup.add(rectMesh);
    scene.add(fanGroup);

    // pointer interaction (hover & click) for select mode
    // set a small line threshold so raycasting better matches thin screen lines
    try {
      (raycasterRef.current.params as any).Line = (raycasterRef.current.params as any).Line || {};
      (raycasterRef.current.params as any).Line.threshold = 0.02; // tune as needed
    } catch (e) {
      // ignore if params not writable
    }

    //select segment mode
    const handlePointerMove = (event: PointerEvent) => {
      if (!selectModeRef.current) {
        renderer.domElement.style.cursor = 'default';
        return;
      }
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(new THREE.Vector2(x, y), camera);
      // only consider visible lines; this prevents picking objects hidden by toggles
      const objects = [
        ...segRefGroup.children.filter(c => c.visible),
        ...segPredGroup.children.filter(c => c.visible),
        ...segLabelGroup.children.filter(c => c.visible),
      ];
      const intersects = raycasterRef.current.intersectObjects(objects, false);
      if (intersects.length > 0) {
        const intersect = intersects[0].object;
        if (hoveredLineRef.current !== intersect) {
          // dim others (only visible ones)
          objects.forEach(obj => {
            const mat = (obj as THREE.Line).material as THREE.LineBasicMaterial;
            if (mat) {
              mat.transparent = true;
              mat.opacity = 0.15;
              (mat as any).linewidth = 1;
              mat.needsUpdate = true;
            }
          });
          const mat = (intersect as THREE.Line).material as THREE.LineBasicMaterial;
          if (mat) {
            mat.transparent = true;
            mat.opacity = 1;
            (mat as any).linewidth = 3;
            mat.needsUpdate = true;
          }
          hoveredLineRef.current = intersect;
        }
        renderer.domElement.style.cursor = 'pointer';
      } else {
        // not hovering any line
        objects.forEach(obj => {
          const mat = (obj as THREE.Line).material as THREE.LineBasicMaterial;
          if (mat) {
            mat.transparent = true;
            mat.opacity = 0.4;
            (mat as any).linewidth = 1;
            mat.needsUpdate = true;
          }
        });
        hoveredLineRef.current = null;
        renderer.domElement.style.cursor = 'default';
      }
    };

    //click for segment select navigation
    const handleClick = (event: PointerEvent) => {
      if (!selectModeRef.current || !hoveredLineRef.current) return;
      const { frameIcid, dicomIcid: lineDicom } = hoveredLineRef.current.userData || {};
      const destFrame = frameIcid;
      if (!destFrame) return;
      const destDicom = lineDicom || dicomIcidRef.current;
      navigate(`/${studyIcid}/${destDicom}/${destFrame}`);
    };

    renderer.domElement.addEventListener('pointermove', handlePointerMove);
    renderer.domElement.addEventListener('pointerdown', handleClick);

    // Save to refs
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;
    segRefGroupRef.current = segRefGroup;
    segPredGroupRef.current = segPredGroup;
    segLabelGroupRef.current = segLabelGroup;

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      const { clientWidth, clientHeight } = containerRef.current;
      if (clientWidth === 0 || clientHeight === 0) return;

      cameraRef.current.aspect = clientWidth / clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(clientWidth, clientHeight);
    };

    window.addEventListener('resize', handleResize);

    // Animation loop
    let animationFrameId: number;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);

      controls.dispose();
      renderer.dispose();

      // Remove canvas
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }

      // remove pointer interaction listeners
      try {
        renderer.domElement.removeEventListener('pointermove', handlePointerMove);
        renderer.domElement.removeEventListener('pointerdown', handleClick);
      } catch (e) {
        // ignore if handlers not attached or already removed
      }

      // Dispose scene objects
      scene.traverse(obj => {
        if ((obj as THREE.Mesh).geometry) {
          (obj as THREE.Mesh).geometry.dispose();
        }
        if ((obj as THREE.Mesh).material) {
          const mat = (obj as THREE.Mesh).material;
          if (Array.isArray(mat)) {
            mat.forEach(m => m.dispose());
          } else {
            mat.dispose();
          }
        }
      });

      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      controlsRef.current = null;
      segRefGroupRef.current = null;
    };
  }, []); // IMPORTANT: empty deps → no re-init on prop changes

  // update fan position when activeFrame or pnoResponse changes
  useEffect(() => {
    if (
      !sceneRef.current ||
      !pnoResponse ||
      pnoResponse.length === 0 ||
      !activeFrame ||
      !activeFrame.icid
    )
      return;

    const fanGroup = fanGroupRef.current;
    if (!fanGroup) return;

    //find in pnoMap
    const pno = pnoMap.get(activeFrame.icid);
    if (!pno) return;
    const pnoMatrix = buildPnoMatrixFan(pno, imageHeight, imageWidth);
    fanGroup.matrixAutoUpdate = false;
    fanGroup.matrix.copy(pnoMatrix);
    fanGroup.updateMatrixWorld(true);
  }, [activeFrame, pnoResponse, imageHeight, imageWidth]);

  // update texture of fan mesh when activeFrame changes
  useEffect(() => {
    const activeFrameIcid = activeFrame?.icid;
    const activeFrameFile = activeFrame?.file;
    if (!fanGroupRef.current || !activeFrameIcid || !activeFrameFile) return;
    const fanMesh = fanGroupRef.current?.children[1] as THREE.Mesh;
    if (!fanMesh || !fanMesh.material) return;
    const material = fanMesh.material as THREE.MeshBasicMaterial;

    // Check cache first
    if (textureCache.current.has(activeFrameIcid)) {
      const cachedTexture = textureCache.current.get(activeFrameIcid);
      if (!cachedTexture) return;
      material.map = cachedTexture;
      material.needsUpdate = true;
      material.side = THREE.DoubleSide;
      return;
    }

    // Load and cache
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(activeFrameFile, texture => {
      texture.flipY = false;
      texture.repeat.x = -1;
      texture.offset.x = 1;
      textureCache.current.set(activeFrameIcid, texture);
      material.map = texture;
      material.needsUpdate = true;
      material.side = THREE.DoubleSide;
    });
  }, [activeFrame?.icid]);

  function buildClassColorMap(
    segs: Array<ISegmentationLabel | ISegmentationReference | ISegmentationPrediction>
  ): Record<string, THREE.Color> {
    const classSet = new Set<string>();
    for (const seg of segs) {
      classSet.add(seg.segmentationClassValue ?? 'Unknown');
    }
    const classes = Array.from(classSet).sort();
    const map: Record<string, THREE.Color> = {};
    const n = classes.length;
    classes.forEach((cls, i) => {
      const hue = ((i * 360) / n + 255) % 360;
      map[cls] = new THREE.Color(`hsl(${hue}, 80%, 70%)`);
    });
    return map;
  }

  // --- Update segmentation lines when allSegmentations changes ---
  function updateSegmentationGroup(
    segs:
      | Array<ISegmentationLabel | ISegmentationReference | ISegmentationPrediction>
      | null
      | undefined,
    segGroup: THREE.Group,
    sceneRef: React.RefObject<THREE.Scene | null>,
    controlsRef: React.RefObject<any>,
    cameraRef: React.RefObject<THREE.Camera | null>
  ) {
    if (!segs || segs.length === 0) return;
    if (!sceneRef.current || !controlsRef.current) return;

    const svgLoader = new SVGLoader();

    // --- dispose old children ---
    segGroup.children.forEach(child => {
      const line = child as THREE.Line;
      line.geometry?.dispose();
      if (line.material) {
        Array.isArray(line.material)
          ? line.material.forEach(m => m.dispose())
          : line.material.dispose();
      }
    });
    segGroup.clear();

    // --- build geometry ---
    segs.forEach(seg => {
      if (!seg?.paths?.length) return;
      if (!seg?.pno) return;
      const color = segClassColorMap[seg.segmentationClassValue || 'Unknown'];
      seg.paths.forEach(pathStr => {
        try {
          const svgData = svgLoader.parse(
            `<svg xmlns="http://www.w3.org/2000/svg">
              <path d="${pathStr.replace(/,/g, ' ')}" />
            </svg>`
          );

          svgData.paths.forEach(path => {
            const shapes = path.toShapes(true);
            shapes.forEach(shape => {
              const finalMatrix = buildPnoMatrixSegmentation(seg.pno, IMAGE_HEIGHT, IMAGE_WIDTH);
              const pts = shape.getPoints(100);
              const geom = new THREE.BufferGeometry().setFromPoints(
                pts.map(pt => new THREE.Vector3(pt.x * SCALE, -pt.y * SCALE, 0))
              );
              geom.applyMatrix4(finalMatrix);
              const mat = new THREE.LineBasicMaterial({
                color: color.clone().offsetHSL(0, 0, -0.2),
                linewidth: 1,
                transparent: true,
                opacity: selectModeRef.current ? 0.4 : 1,
              });
              const line = new THREE.LineLoop(geom, mat);
              line.userData.segClass = seg.segmentationClassValue || 'Unknown';
              // attach routing info where available
              line.userData.frameIcid = seg.frameIcid;
              line.userData.dicomIcid = seg.dicomIcid ?? dicomIcidRef.current;
              segGroup.add(line);
            });
          });
        } catch (e) {
          console.error('Error parsing SVG path:', pathStr, e);
        }
      });
    });

    // --- center camera ---
    const center = computeGroupCenter(segGroup);
    controlsRef.current.target.copy(center);
    cameraRef.current?.lookAt(center);
    controlsRef.current.update();
  }

  useEffect(() => {
    if (!segRefGroupRef.current) return;
    updateSegmentationGroup(
      allSegmentationRefsResponse,
      segRefGroupRef.current,
      sceneRef,
      controlsRef,
      cameraRef
    );
  }, [allSegmentationRefsResponse, segClassColorMap]);

  useEffect(() => {
    if (!segPredGroupRef.current) return;
    updateSegmentationGroup(
      allSegmentationPredsResponse,
      segPredGroupRef.current,
      sceneRef,
      controlsRef,
      cameraRef
    );
  }, [allSegmentationPredsResponse, segClassColorMap]);

  useEffect(() => {
    if (!segLabelGroupRef.current) return;
    updateSegmentationGroup(
      allSegmentationLabelsResponse,
      segLabelGroupRef.current,
      sceneRef,
      controlsRef,
      cameraRef
    );
  }, [allSegmentationLabelsResponse, segClassColorMap]);

  useEffect(() => {
    const groups = [segLabelGroupRef.current, segRefGroupRef.current, segPredGroupRef.current];
    groups.forEach(group => {
      if (!group) return;
      group.children.forEach(child => {
        const cls = child.userData?.segClass;
        if (!cls) return;
        child.visible = visibleSegClasses[cls] ?? true;
      });
    });
  }, [visibleSegClasses]);

  return (
    <ViewerContainer ref={containerRef}>
      <Toolbar>
        <ModeButton active={showSegLabel} onClick={toggleSegLabelVisibility}>
          TECHNICIAN
        </ModeButton>
        <ModeButton active={showSegRef} onClick={toggleSegRefVisibility}>
          REF
        </ModeButton>
        <ModeButton active={showSegPred} onClick={toggleSegPredVisibility}>
          PRED
        </ModeButton>
        <ModeButton
          active={selectMode}
          onClick={() => setSelectMode(prev => !prev)}
          title="Select Mode"
        >
          👆
        </ModeButton>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Toolbar>
      <BottomBar>
        {Object.entries(segClassColorMap).map(([className, color]) => {
          const isActive = visibleSegClasses[className] ?? true;
          return (
            <SegChipButton
              key={className}
              $bg={`#${color.getHexString()}`}
              $active={isActive}
              onClick={() => toggleSegClassVisibility(className)}
              title={className}
              type="button"
            >
              {SEGMENTATION_CLASS_ACRONYM[className] ?? className}
            </SegChipButton>
          );
        })}
      </BottomBar>
    </ViewerContainer>
  );
};

export default ThreeDViewer;
